import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface AffiliateProgram {
  id: string;
  program_name: string;
  default_commission_rate: number;
  commission_type: string;
  cookie_duration_days: number;
  minimum_payout: number;
  payment_terms: string;
  created_at: string;
}

interface AffiliateSignupFormProps {
  program: AffiliateProgram | null;
}

interface FormSettings {
  form_title: string;
  form_description: string;
  logo_url: string;
  primary_color: string;
  success_message: string;
  terms_and_conditions: string;
  privacy_policy_url: string;
  redirect_url: string;
  is_active: boolean;
}

export const AffiliateSignupForm: React.FC<AffiliateSignupFormProps> = ({ program }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [formId, setFormId] = useState<string | null>(null);
  const [shareableLink, setShareableLink] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [formSettings, setFormSettings] = useState<FormSettings>({
    form_title: 'Join Our Affiliate Program',
    form_description: 'Partner with us and earn commissions for every customer you refer.',
    logo_url: '',
    primary_color: '#3b82f6', // Tailwind blue-500
    success_message: 'Thank you for your application! We will review it shortly.',
    terms_and_conditions: 'By submitting this form, you agree to our affiliate terms and conditions.',
    privacy_policy_url: '',
    redirect_url: '',
    is_active: true
  });

  useEffect(() => {
    if (program) {
      loadFormSettings();
    } else {
      setLoading(false);
    }
  }, [program]);

  const loadFormSettings = async () => {
    try {
      setLoading(true);
      if (!program) {
        toast.error('No program found');
        setLoading(false);
        return;
      }

      const data = await affiliateService.getSignupForm(program.id);

      if (data?.form) {
        setFormId(data.form.id);
        setFormSettings({
          form_title: data.form.form_title || formSettings.form_title,
          form_description: data.form.form_description || formSettings.form_description,
          logo_url: data.form.logo_url || formSettings.logo_url,
          primary_color: data.form.primary_color || formSettings.primary_color,
          success_message: data.form.success_message || formSettings.success_message,
          terms_and_conditions: data.form.terms_and_conditions || formSettings.terms_and_conditions,
          privacy_policy_url: data.form.privacy_policy_url || formSettings.privacy_policy_url,
          redirect_url: data.form.redirect_url || formSettings.redirect_url,
          is_active: data.form.is_active
        });
        
        // Generate shareable link
        const baseUrl = window.location.origin;
        setShareableLink(`${baseUrl}/affiliate/signup/${data.form.id}`);
      }
    } catch (error) {
      console.error('Error loading signup form settings:', error);
      toast.error('Failed to load signup form settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Handle checkboxes
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormSettings(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormSettings(prev => ({
      ...prev,
      primary_color: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!program) {
      toast.error('No program found');
      return;
    }
    
    try {
      setSaving(true);

      const data = await affiliateService.upsertSignupForm(program.id, formSettings as any, formId);

      if (!formId && data?.form?.id) {
        setFormId(data.form.id);
        const baseUrl = window.location.origin;
        setShareableLink(`${baseUrl}/affiliate/signup/${data.form.id}`);
      }
      
      toast.success('Signup form settings saved successfully');
    } catch (error) {
      console.error('Error saving signup form settings:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save signup form settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    // Use the native Clipboard API
    navigator.clipboard.writeText(shareableLink)
      .then(() => {
        toast.success('Signup form link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy link to clipboard');
      });
  };

  // Preview the signup form as it will appear to affiliates
  const renderFormPreview = () => {
    return (
      <div className="bg-white shadow-lg rounded-lg p-6 max-w-2xl mx-auto">
        {formSettings.logo_url && (
          <div className="flex justify-center mb-6">
            <img src={formSettings.logo_url} alt="Program Logo" className="h-16 w-auto" />
          </div>
        )}
        
        <h2 className="text-2xl font-bold text-center mb-2" style={{ color: formSettings.primary_color }}>
          {formSettings.form_title}
        </h2>
        
        <p className="text-gray-600 text-center mb-6">
          {formSettings.form_description}
        </p>
        
        <form className="space-y-6">
          <div>
            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
              Company Name *
            </label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              placeholder="Your company name"
              disabled
            />
          </div>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700">
                Contact Name *
              </label>
              <input
                type="text"
                id="contact_name"
                name="contact_name"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                placeholder="Your full name"
                disabled
              />
            </div>
            
            <div>
              <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700">
                Contact Email *
              </label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                placeholder="you@example.com"
                disabled
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="website_url" className="block text-sm font-medium text-gray-700">
              Website URL
            </label>
            <input
              type="url"
              id="website_url"
              name="website_url"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              placeholder="https://www.example.com"
              disabled
            />
          </div>
          
          <div>
            <label htmlFor="why_join" className="block text-sm font-medium text-gray-700">
              Why do you want to join our affiliate program?
            </label>
            <textarea
              id="why_join"
              name="why_join"
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              placeholder="Tell us a bit about yourself and why you'd like to become an affiliate..."
              disabled
            />
          </div>
          
          <div className="flex items-center">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
              {formSettings.terms_and_conditions}
            </label>
          </div>
          
          <div>
            <button
              type="button"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ backgroundColor: formSettings.primary_color }}
              disabled
            >
              Submit Application
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Render the form settings
  const renderFormSettings = () => {
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Form Settings</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Customize how your affiliate signup form appears to potential affiliates.
            </p>
          </div>
          
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Form Title</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="form_title"
                    value={formSettings.form_title}
                    onChange={handleChange}
                    className="max-w-lg block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <textarea
                    name="form_description"
                    rows={3}
                    value={formSettings.form_description}
                    onChange={handleChange}
                    className="max-w-lg shadow-sm block w-full focus:ring-blue-500 focus:border-blue-500 sm:text-sm border border-gray-300 rounded-md"
                  />
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Logo URL</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="logo_url"
                    value={formSettings.logo_url}
                    onChange={handleChange}
                    placeholder="https://example.com/logo.png"
                    className="max-w-lg block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the URL of your logo image (recommended size: 200x60px)
                  </p>
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Primary Color</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <input
                      type="color"
                      name="primary_color"
                      value={formSettings.primary_color}
                      onChange={handleColorChange}
                      className="h-8 w-8 border-gray-300 rounded-md"
                    />
                    <input
                      type="text"
                      value={formSettings.primary_color}
                      onChange={handleChange}
                      name="primary_color"
                      className="ml-2 max-w-lg block w-32 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Success Message</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <textarea
                    name="success_message"
                    rows={2}
                    value={formSettings.success_message}
                    onChange={handleChange}
                    className="max-w-lg shadow-sm block w-full focus:ring-blue-500 focus:border-blue-500 sm:text-sm border border-gray-300 rounded-md"
                  />
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Terms & Conditions</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <textarea
                    name="terms_and_conditions"
                    rows={3}
                    value={formSettings.terms_and_conditions}
                    onChange={handleChange}
                    className="max-w-lg shadow-sm block w-full focus:ring-blue-500 focus:border-blue-500 sm:text-sm border border-gray-300 rounded-md"
                  />
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Privacy Policy URL</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="privacy_policy_url"
                    value={formSettings.privacy_policy_url}
                    onChange={handleChange}
                    placeholder="https://example.com/privacy-policy"
                    className="max-w-lg block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Redirect URL (optional)</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="redirect_url"
                    value={formSettings.redirect_url}
                    onChange={handleChange}
                    placeholder="https://example.com/thank-you"
                    className="max-w-lg block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Where to redirect users after they submit the form
                  </p>
                </dd>
              </div>
              
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Form Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <input
                      id="is_active"
                      name="is_active"
                      type="checkbox"
                      checked={formSettings.is_active}
                      onChange={(e) => {
                        setFormSettings(prev => ({
                          ...prev,
                          is_active: e.target.checked
                        }));
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Form is active and accepting submissions
                    </label>
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
        
        {/* Shareable Link Section */}
        {formId && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Shareable Link</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Share this link with potential affiliates to allow them to sign up.
              </p>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Form URL</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex">
                      <input
                        type="text"
                        readOnly
                        value={shareableLink}
                        className="flex-1 max-w-lg shadow-sm sm:text-sm border-gray-300 rounded-l-md"
                      />
                      <button
                        type="button"
                        onClick={handleCopyLink}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 shadow-sm text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                        </svg>
                        Copy
                      </button>
                    </div>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
        
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setPreviewMode(!previewMode)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {previewMode ? 'Back to Settings' : 'Preview Form'}
          </button>
          
          {!previewMode && (
            <button
              type="submit"
              disabled={saving}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : 'Save Settings'}
            </button>
          )}
        </div>
      </form>
    );
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {previewMode ? renderFormPreview() : renderFormSettings()}
    </div>
  );
};
